import {type ModelInfo,type UsageQuery,type UsageStatus} from '@reados/contracts';

export const USAGE_STATUS_LABELS:Record<UsageStatus,string>={running:'进行中',succeeded:'成功',failed:'失败',cancelled:'已取消',interrupted:'已中断'};
export interface UsageFilters {from:string;to:string;model:string;status:UsageStatus|''}
export const EMPTY_USAGE_FILTERS:UsageFilters={from:'',to:'',model:'',status:''};

/** The date controls use the viewer's local calendar, including DST transitions. */
function calendarBoundary(value:string,nextDay=false):string {
  const parts=/^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if(!parts)throw new Error('请选择有效日期。');
  const year=Number(parts[1]),month=Number(parts[2])-1,day=Number(parts[3]);
  const date=new Date(year,month,day);
  if(date.getFullYear()!==year||date.getMonth()!==month||date.getDate()!==day)throw new Error('请选择有效日期。');
  if(nextDay)date.setDate(date.getDate()+1);
  return date.toISOString();
}
export function usageQuery(filters:UsageFilters,page=1,pageSize=25):UsageQuery {
  if(filters.from&&filters.to&&filters.from>filters.to)throw new Error('结束日期不能早于开始日期。');
  return {...(filters.from?{from:calendarBoundary(filters.from)}:{}),...(filters.to?{to:calendarBoundary(filters.to,true)}:{}),...(filters.model?{model:filters.model}:{}),...(filters.status?{status:filters.status}:{}),page,pageSize};
}
export function usageSearchParams(query:UsageQuery):string {
  const params=new URLSearchParams();
  for(const key of ['from','to','model','status','page','pageSize'] as const){const value=query[key];if(value!==undefined)params.set(key,String(value));}
  return params.toString();
}
export const usageNumber=(value:number|null|undefined)=>typeof value==='number'&&Number.isFinite(value)&&value>=0?value.toLocaleString('zh-CN'):'—';
/** Display exact nano-CNY values without a floating-point conversion or rounding away tiny fees. */
export function usageMoney(value:string|null|undefined):string {
  if(typeof value!=='string'||!/^\d+$/.test(value))return '—';
  const nano=BigInt(value),whole=nano/1_000_000_000n;
  const fraction=(nano%1_000_000_000n).toString().padStart(9,'0').replace(/0+$/,'').padEnd(2,'0');
  return `¥${whole.toLocaleString('zh-CN')}.${fraction}`;
}
/** Unit prices come from the server's tariff, not a second client-side price catalog. */
export function usageUnitPrice(value:number|null|undefined):string {
  return typeof value==='number'&&Number.isFinite(value)&&value>=0?`¥${value.toLocaleString('zh-CN',{minimumFractionDigits:2,maximumFractionDigits:9})}`:'—';
}

export function usageDuration(value:number|null|undefined):string {
  if(typeof value!=='number'||!Number.isFinite(value)||value<0)return '—';
  if(value<1000)return `${Math.round(value)} ms`;
  if(value<60_000)return `${(value/1000).toFixed(value<10_000?1:0)} s`;
  const seconds=Math.floor(value/1000);
  return `${Math.floor(seconds/60)} 分 ${seconds%60} 秒`;
}
export function usageTimestamp(value:string|null):string {
  if(!value)return '—';const date=new Date(value);
  if(!Number.isFinite(date.getTime()))return '—';
  return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')} ${String(date.getHours()).padStart(2,'0')}:${String(date.getMinutes()).padStart(2,'0')}:${String(date.getSeconds()).padStart(2,'0')}`;
}
export const modelTone=(model:string)=>model==='gpt-6-astra'?'astra':model==='gpt-6.1-sol'?'sol61':model==='gpt-5.6-sol'?'sol56':'unknown';

/** Metadata and availability come from the server-owned catalog. */
export function allowedModelCatalog(models:ModelInfo[],defaultModelId?:string):ModelInfo[] {
  const seen=new Set<string>(),catalog:ModelInfo[]=[];
  for(const model of models){if(typeof model.id!=='string'||!model.id.length||model.enabled===false||seen.has(model.id))continue;seen.add(model.id);catalog.push(model);}
  const effectiveDefault=catalog.find(model=>model.id===defaultModelId)?.id??catalog.find(model=>model.isDefault===true)?.id;
  return catalog.map(model=>({...model,...(effectiveDefault?{isDefault:model.id===effectiveDefault}:{})}));
}
export function selectAllowedModel(value:unknown,models:ModelInfo[]):string {
  if(typeof value==='string'&&models.some(model=>model.id===value))return value;
  return models.find(model=>model.isDefault===true)?.id??models[0]?.id??'';
}
/** Include previously seen request IDs so disabled/renamed catalog entries remain filterable. */
export function usageModelOptions(...groups:Array<readonly string[]>):string[]{return [...new Set(groups.flat().filter(value=>typeof value==='string'&&value.length>0))].sort((a,b)=>a.localeCompare(b));}
export function usageModelTitle(model:string,names:Readonly<Record<string,string>>={}):string {
 const name=Object.hasOwn(names,model)?names[model]:undefined;
 return name||(/^model-[0-9a-f-]{36}$/i.test(model)?'模型配置':model);
}

/** Account balances can be negative after an in-flight request settles. Preserve the sign exactly. */
export function usageSignedMoney(value:string|null|undefined):string {
  if(typeof value!=='string'||!/^\-?\d+$/.test(value))return '—';
  const amount=BigInt(value);
  return `${amount<0n?'-':''}${usageMoney((amount<0n?-amount:amount).toString())}`;
}
